/**
 * CallTrackingMetrics REST Helpers (Story 22.3, AC #4, AC #5, AC #7, AC #12)
 *
 * Per-endpoint typed wrappers around `safeFetchCtm`. Each helper:
 *  - Builds the CTM v1 URL from typed params (account-scoped paths
 *    with mandatory `.json` suffix on every endpoint per AC #12).
 *  - Validates required params (throws `TypeError` synchronously on
 *    missing `startDate` / `endDate` and on `perPage > 150`).
 *  - Calls `safeFetchCtm` for dual-secret-injected, sanitized fetch.
 *  - Parses the response body via `expectedRecordsPath` shape-check.
 *  - Throws `CtmApiCallError` on HTTP error mapping per AC #7.
 *
 * **Web research divergences from the story AC text** (verified
 * `postman.calltrackingmetrics.com/` + the API users guide on GitHub
 * 2026-05-15):
 *
 *  1. **Pagination uses a full-URL `next_page` cursor** — NOT a page
 *     number. The walker in `pagination.ts` follows the cursor verbatim
 *     rather than reconstructing from base + query params.
 *
 *  2. **Mandatory `.json` suffix on every endpoint** — `/accounts.json`,
 *     `/calls.json`, `/forms.json`, `/sms.json`. URLs without `.json`
 *     return content-negotiation errors. Enforced by a build-time test.
 *
 *  3. **per_page=150 hard ceiling** — `400 "per_page limit is 150"`
 *     if exceeded. Adapter clamps client-side BEFORE building the URL.
 *
 *  4. **Region selector** — adapter URLs are computed from
 *     `baseUrlFor(region)` per AC #2 step-1 region detection. EU
 *     hostname is runtime-probed at connect time; once stamped on
 *     `metadata.region`, all subsequent calls go to the chosen region.
 *
 *  5. **Sale endpoint deferred** — Story 22.3 reads `record.sale`
 *     opportunistically (Endpoint Group D per AC #4) when CTM inlines
 *     it on the `/calls.json` response. The N+1 per-call sale endpoint
 *     enrichment (`/calls/{id}/sale.json`) is deferred to Story 22.5+.
 */

import {
  CtmApiCallError,
  parseCtmApiError,
  sanitizeCtmErrorMessage,
} from "./errors";
import { safeFetchCtm } from "./safe-fetch";
import { CTM_PER_PAGE_MAX, computeCtmFields } from "./fields";
import { baseUrlFor, type CtmRegion } from "./region";
import {
  walkCtmPagination,
  type PaginationPageResult,
} from "./pagination";
import {
  transformCtmCallRow,
  transformCtmFormSubmissionRow,
  transformCtmSmsRow,
  transformCtmTriggerRow,
  transformCtmIntegrationRow,
} from "./transformers";

// ============================================================================
// Constants
// ============================================================================

/**
 * Re-export the per-page max for adapter-internal use. Callers should
 * clamp BEFORE invoking the helpers; the helpers also clamp defensively
 * + throw on overflow.
 */
export { CTM_PER_PAGE_MAX };

// ============================================================================
// Account discovery — AC #2
// ============================================================================

/**
 * Single CTM account as returned by `/accounts.json`. The docs document
 * additional fields (`status`, `created_at`, `parent_id`, etc.) — we
 * surface the canonical 3 for the picker UI and let the route handler
 * stash the full shape in KV side-channel if needed.
 */
export interface CtmAccount {
  readonly id: string;
  readonly name: string;
  readonly status?: string;
}

export interface DiscoverCtmAccountsResult {
  readonly accounts: ReadonlyArray<CtmAccount>;
  readonly totalRecords: number;
  /**
   * Effective region after possible EU→US fallback per Round 2
   * Pre-mortem B. May DIFFER from the requested region.
   */
  readonly region: CtmRegion;
}

/**
 * Discover accessible CTM accounts for the given Access Key + Secret
 * Key. The single canonical entitlement-probe call —
 * `GET /accounts.json` costs only a single rate-limit-quota request
 * (CTM bills only by request count).
 *
 * **Region fallback per AC #2:** if `region === "eu"` AND the call
 * fails with DNS error (`ENOTFOUND`) OR 5xx, the helper retries once
 * with `region: "us"` AND emits a `ctm_eu_region_probe_failed`
 * structured-log warning. The returned `region` field reflects the
 * effective region after fallback.
 *
 * @param accessKey - The plaintext Access Key (one of two secrets).
 * @param secretKey - The plaintext Secret Key (one of two secrets).
 * @param region - Requested region (`"us"` or `"eu"`).
 * @throws `CtmApiCallError` on HTTP error after fallback.
 */
export async function discoverCtmAccounts(
  accessKey: string,
  secretKey: string,
  region: CtmRegion,
): Promise<DiscoverCtmAccountsResult> {
  const tryRegion = async (r: CtmRegion): Promise<{
    accounts: CtmAccount[];
    totalRecords: number;
    pagesWalked: number;
  }> => {
    const accounts: CtmAccount[] = [];

    const fetchPage = async (
      nextPageUrl: string | undefined,
    ): Promise<PaginationPageResult<CtmAccount>> => {
      const url =
        nextPageUrl ??
        `${baseUrlFor(r)}/accounts.json?per_page=${CTM_PER_PAGE_MAX}`;
      const result = await safeFetchCtm(url, accessKey, secretKey, {
        method: "GET",
      });
      if (!result.success) {
        throw wrapTransportError(result.error, accessKey, secretKey);
      }
      const response = result.data;
      if (!response.ok) {
        const body = await safeReadBody(response);
        throw new CtmApiCallError(
          parseCtmApiError(response.status, body, accessKey, secretKey),
        );
      }
      const body = await safeReadJson(response);
      if (
        !body ||
        !Array.isArray((body as { accounts?: unknown }).accounts)
      ) {
        throw new CtmApiCallError({
          type: "unknown",
          message: sanitizeCtmErrorMessage(
            "CTM /accounts.json response missing `accounts` array",
            accessKey,
            secretKey,
          ),
          status: response.status,
          retryable: false,
        });
      }
      const raw = body as {
        accounts: ReadonlyArray<Record<string, unknown>>;
        total_entries?: number;
        total_pages?: number;
        next_page?: string | null;
      };
      const pageRecords: CtmAccount[] = [];
      for (const a of raw.accounts) {
        const id = String(a["id"] ?? "");
        if (id.length === 0) continue;
        pageRecords.push({
          id,
          name: String(a["name"] ?? ""),
          status:
            typeof a["status"] === "string"
              ? (a["status"] as string)
              : undefined,
        });
      }
      return {
        records: pageRecords,
        nextPageUrl:
          typeof raw.next_page === "string" && raw.next_page.length > 0
            ? raw.next_page
            : null,
        totalEntries: raw.total_entries,
        totalPages: raw.total_pages,
      };
    };

    // Cast: CtmAccount is a structural subtype with known fields but
    // the walker's generic constraint is Record<string, unknown> for
    // dedup-by-id support. CtmAccount has `id: string` which satisfies
    // the dedup contract.
    const walk = await walkCtmPagination<CtmAccount & Record<string, unknown>>(
      fetchPage as (
        nextPageUrl: string | undefined,
      ) => Promise<PaginationPageResult<CtmAccount & Record<string, unknown>>>,
    );
    accounts.push(...walk.records);
    return {
      accounts,
      totalRecords: walk.totalEntries || accounts.length,
      pagesWalked: walk.pagesWalked,
    };
  };

  // Region fallback: try EU first if requested; on DNS / 5xx, fall back to US.
  if (region === "eu") {
    try {
      const result = await tryRegion("eu");
      return { ...result, region: "eu" };
    } catch (err) {
      if (shouldFallBackToUs(err)) {
        // Round 2 Pre-mortem B — emit structured-log warning.
        emitCtmEuRegionProbeFailed(err);
        const result = await tryRegion("us");
        return { ...result, region: "us" };
      }
      throw err;
    }
  }

  const result = await tryRegion("us");
  return { ...result, region: "us" };
}

/**
 * Predicate: is the given error eligible for the EU→US fallback per
 * Round 2 Pre-mortem B? Returns true for DNS failures (`ENOTFOUND` /
 * `EAI_AGAIN`) and 5xx responses.
 */
function shouldFallBackToUs(err: unknown): boolean {
  if (err instanceof CtmApiCallError) {
    return err.status >= 500;
  }
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    return (
      msg.includes("enotfound") ||
      msg.includes("eai_again") ||
      msg.includes("dns") ||
      msg.includes("getaddrinfo")
    );
  }
  return false;
}

/**
 * Emit a `ctm_eu_region_probe_failed` structured-log warning. Uses
 * `console.warn` for visibility; structured-log integrations (Sentry /
 * Logflare) ingest these.
 */
function emitCtmEuRegionProbeFailed(err: unknown): void {
  const message = err instanceof Error ? err.message : String(err);
  console.warn(
    JSON.stringify({
      event: "ctm_eu_region_probe_failed",
      message: message.slice(0, 500),
      timestamp: new Date().toISOString(),
    }),
  );
}

/**
 * Connection-validation helper. Calls `discoverCtmAccounts` and
 * returns a typed result. Caller (route or health-check) handles the
 * zero-accounts case + decides what to do with the discovered list.
 */
export async function validateCtmConnection(
  accessKey: string,
  secretKey: string,
  region: CtmRegion,
): Promise<{
  readonly ok: true;
  readonly accounts: ReadonlyArray<CtmAccount>;
  readonly totalRecords: number;
  readonly region: CtmRegion;
}> {
  const { accounts, totalRecords, region: effectiveRegion } =
    await discoverCtmAccounts(accessKey, secretKey, region);
  return { ok: true, accounts, totalRecords, region: effectiveRegion };
}

// ============================================================================
// Conversation Intelligence entitlement probe — AC #2
// ============================================================================

export type CtmCiEntitlement = "entitled" | "not_entitled" | "unknown";

export interface ProbeCtmCiResult {
  readonly entitlement: CtmCiEntitlement;
  readonly status: number;
}

/**
 * Probe whether the customer has Conversation Intelligence entitled.
 *
 * Implementation: call `GET /accounts/{firstAccountId}/calls.json?per_page=1&fields=transcription,summary,likelihood`.
 *  - 200 with `calls[0].transcription` is a populated string → `entitled`
 *  - 200 with `calls.length === 0` → `unknown` (no data to test against)
 *  - 200 with `calls[0].transcription === null` for populated calls →
 *    `unknown` (CI may be entitled but transcription hasn't been
 *    computed yet — CI processing lags after call end)
 *  - 403 / 422 → `not_entitled` (subscription gate)
 *  - Any other error → re-throws via `CtmApiCallError`
 */
export async function probeCtmConversationIntelligence(
  accessKey: string,
  secretKey: string,
  region: CtmRegion,
  firstAccountId: string,
): Promise<ProbeCtmCiResult> {
  const url = `${baseUrlFor(region)}/accounts/${encodeURIComponent(firstAccountId)}/calls.json?per_page=1&fields=transcription,summary,likelihood`;
  const result = await safeFetchCtm(url, accessKey, secretKey, {
    method: "GET",
  });
  if (!result.success) {
    // 4xx/5xx responses surface as `success: false` with a
    // `providerStatus`. Subscription gates (403 / 422) → not_entitled
    // (the canonical signal). 401 / 5xx → re-throw via
    // CtmApiCallError so the route handler can surface the right user
    // message (invalid_credentials vs ctm_unavailable). Pure transport
    // errors (DNS, abort) have no providerStatus → return unknown.
    const status = result.error.providerStatus ?? 0;
    if (status === 403 || status === 422) {
      return { entitlement: "not_entitled", status };
    }
    if (status === 0) {
      // Pure transport error — degrade gracefully so the probe doesn't
      // block the connect flow on a transient network hiccup.
      return { entitlement: "unknown", status: 0 };
    }
    throw new CtmApiCallError({
      type: status === 401 ? "unauthorized" : "provider_unavailable",
      message: result.error.message,
      status,
      retryable: result.error.retryable,
      providerMessage: result.error.providerMessage,
    });
  }
  const response = result.data;
  const body = await safeReadJson(response);
  const calls = (body as { calls?: ReadonlyArray<Record<string, unknown>> })
    ?.calls;
  if (!Array.isArray(calls) || calls.length === 0) {
    return { entitlement: "unknown", status: response.status };
  }
  const first = calls[0];
  // Treat a non-empty transcription string as `entitled`. Otherwise
  // `unknown` — the 403/422 branch above is the authoritative
  // `not_entitled` signal. Carries forward CallRail 22.1 Pass 1 review
  // fix: misclassifying as `not_entitled` would permanently strip CI
  // fields from sync requests until reconnect.
  if (
    first &&
    typeof first["transcription"] === "string" &&
    (first["transcription"] as string).length > 0
  ) {
    return { entitlement: "entitled", status: response.status };
  }
  return { entitlement: "unknown", status: response.status };
}

// ============================================================================
// Data endpoint helpers
// ============================================================================

export interface FetchCtmCallsParams {
  readonly accessKey: string;
  readonly secretKey: string;
  readonly region: CtmRegion;
  readonly accountId: string;
  readonly startDate: string; // ISO 8601 (YYYY-MM-DD)
  readonly endDate: string;
  /** Default 100; clamped to `CTM_PER_PAGE_MAX` (150) at the helper. */
  readonly perPage?: number;
  /** Optional explicit next_page cursor URL (per-page-only — the paginating wrapper uses this internally). */
  readonly nextPageUrl?: string;
  readonly conversationIntelligenceEntitled: boolean;
  /**
   * Story 35.4 — LeadReactor entitlement flag. Strips LeadReactor-gated
   * fields (`reactor_flag`, `reactor_trigger_name`, `reactor_actions_count`,
   * `reactor_last_fired`) from the `/calls.json` `fields=` URL param when
   * false. Optional; defaults to `false`.
   */
  readonly leadReactorsEnabled?: boolean;
  /**
   * Story 35.4 — Scorecards add-on entitlement flag. Strips
   * Scorecard-gated fields from the `/calls.json` `fields=` URL param
   * when false. Optional; defaults to `false`. The umbrella `scorecards`
   * field is ALSO in `CTM_CI_GATED_FIELDS` so either flag false strips it.
   */
  readonly scorecardsEnabled?: boolean;
}

export interface CtmCallsResponse {
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly rowsCharged: number;
  readonly hasNextPage: boolean;
  readonly nextPage: string | null;
}

/**
 * `GET /api/v1/accounts/{account_id}/calls.json`
 *
 * Returns paginated per-call records. The transformer STRIPS audio URLs
 * (Round 3 Red Team Attack #2). PII fields flow through to the
 * persisted record shape (tenant-scoped); log emissions must run
 * `sanitizeCtmPii` before output.
 *
 * Field selection: when `conversationIntelligenceEntitled === true`,
 * the full Story-22.3 allowlist is requested; when false, the 6
 * CI-gated fields are stripped (per AC #4 + `computeCtmFields`).
 */
export async function fetchCtmCalls(
  params: FetchCtmCallsParams,
): Promise<CtmCallsResponse> {
  requireDateRange(params.startDate, params.endDate);
  const perPage = clampPerPage(params.perPage ?? 100);
  const url =
    params.nextPageUrl ??
    buildCtmCallsUrl({ ...params, perPage });
  const result = await safeFetchCtm(
    url,
    params.accessKey,
    params.secretKey,
    { method: "GET" },
  );
  if (!result.success) {
    throw wrapTransportError(result.error, params.accessKey, params.secretKey);
  }
  const response = result.data;
  if (!response.ok) {
    const body = await safeReadBody(response);
    throw new CtmApiCallError(
      parseCtmApiError(
        response.status,
        body,
        params.accessKey,
        params.secretKey,
      ),
    );
  }
  const body = (await safeReadJson(response)) as Record<string, unknown> | null;
  if (!body) {
    throw new CtmApiCallError({
      type: "unknown",
      message: sanitizeCtmErrorMessage(
        "CTM /calls.json response was empty or not JSON",
        params.accessKey,
        params.secretKey,
      ),
      status: response.status,
      retryable: false,
    });
  }
  const calls = Array.isArray(body["calls"])
    ? (body["calls"] as ReadonlyArray<Record<string, unknown>>)
    : [];
  const records = calls.map((row) =>
    transformCtmCallRow(row, {
      accountId: params.accountId,
    }),
  );
  const nextPage =
    typeof body["next_page"] === "string" &&
    (body["next_page"] as string).length > 0
      ? (body["next_page"] as string)
      : null;
  return {
    records,
    rowsCharged: 1,
    hasNextPage: nextPage !== null,
    nextPage,
  };
}

/**
 * Drain `/calls.json` pagination up to `CTM_PAGINATION_MAX_PAGES` (20
 * pages = 3,000 records). Returns the union of all records across pages
 * plus a `truncated` flag if the cap was hit.
 */
export async function fetchAllCtmCalls(
  params: FetchCtmCallsParams,
): Promise<{
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly truncated: boolean;
  readonly pagesWalked: number;
  readonly totalEntries: number;
}> {
  const perPage = clampPerPage(params.perPage ?? CTM_PER_PAGE_MAX);
  const fetchPage = async (
    nextPageUrl: string | undefined,
  ): Promise<PaginationPageResult<Record<string, unknown>>> => {
    const page = await fetchCtmCalls({ ...params, perPage, nextPageUrl });
    return {
      records: page.records,
      nextPageUrl: page.nextPage,
    };
  };
  const walk = await walkCtmPagination<Record<string, unknown>>(fetchPage);
  return {
    records: walk.records,
    truncated: walk.truncated,
    pagesWalked: walk.pagesWalked,
    totalEntries: walk.totalEntries,
  };
}

export interface FetchCtmFormSubmissionsParams {
  readonly accessKey: string;
  readonly secretKey: string;
  readonly region: CtmRegion;
  readonly accountId: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly perPage?: number;
  readonly nextPageUrl?: string;
}

export interface CtmFormSubmissionsResponse {
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly rowsCharged: number;
  readonly hasNextPage: boolean;
  readonly nextPage: string | null;
}

/**
 * `GET /api/v1/accounts/{account_id}/forms.json`
 *
 * Returns paginated form-submission records. Per the catalogue line
 * 265 the endpoint may also be reachable as `/form_reactors.json`;
 * Story 22.3 uses `/forms.json` as the canonical path. The helper
 * accepts BOTH `form_submissions` and `forms` as the response array
 * key with a `ctm_forms_response_key_mismatch` log event on fallback —
 * defensive against undocumented schema variation.
 */
export async function fetchCtmFormSubmissions(
  params: FetchCtmFormSubmissionsParams,
): Promise<CtmFormSubmissionsResponse> {
  requireDateRange(params.startDate, params.endDate);
  const perPage = clampPerPage(params.perPage ?? 100);
  const url =
    params.nextPageUrl ?? buildCtmFormsUrl({ ...params, perPage });
  const result = await safeFetchCtm(
    url,
    params.accessKey,
    params.secretKey,
    { method: "GET" },
  );
  if (!result.success) {
    throw wrapTransportError(result.error, params.accessKey, params.secretKey);
  }
  const response = result.data;
  if (!response.ok) {
    const body = await safeReadBody(response);
    throw new CtmApiCallError(
      parseCtmApiError(
        response.status,
        body,
        params.accessKey,
        params.secretKey,
      ),
    );
  }
  const body = (await safeReadJson(response)) as Record<string, unknown> | null;
  if (!body) {
    throw new CtmApiCallError({
      type: "unknown",
      message: sanitizeCtmErrorMessage(
        "CTM /forms.json response was empty or not JSON",
        params.accessKey,
        params.secretKey,
      ),
      status: response.status,
      retryable: false,
    });
  }
  let forms = Array.isArray(body["form_submissions"])
    ? (body["form_submissions"] as ReadonlyArray<Record<string, unknown>>)
    : null;
  if (forms === null && Array.isArray(body["forms"])) {
    // Defensive fallback per the catalogue line 265 — emit a log
    // event so operations can spot schema drift.
    console.warn(
      JSON.stringify({
        event: "ctm_forms_response_key_mismatch",
        primary: "form_submissions",
        fallback: "forms",
        timestamp: new Date().toISOString(),
      }),
    );
    forms = body["forms"] as ReadonlyArray<Record<string, unknown>>;
  }
  if (forms === null) forms = [];
  const records = forms.map((row) =>
    transformCtmFormSubmissionRow(row, {
      accountId: params.accountId,
    }),
  );
  const nextPage =
    typeof body["next_page"] === "string" &&
    (body["next_page"] as string).length > 0
      ? (body["next_page"] as string)
      : null;
  return {
    records,
    rowsCharged: 1,
    hasNextPage: nextPage !== null,
    nextPage,
  };
}

/**
 * Drain `/forms.json` pagination up to `CTM_PAGINATION_MAX_PAGES`.
 */
export async function fetchAllCtmFormSubmissions(
  params: FetchCtmFormSubmissionsParams,
): Promise<{
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly truncated: boolean;
  readonly pagesWalked: number;
  readonly totalEntries: number;
}> {
  const perPage = clampPerPage(params.perPage ?? CTM_PER_PAGE_MAX);
  const fetchPage = async (
    nextPageUrl: string | undefined,
  ): Promise<PaginationPageResult<Record<string, unknown>>> => {
    const page = await fetchCtmFormSubmissions({
      ...params,
      perPage,
      nextPageUrl,
    });
    return {
      records: page.records,
      nextPageUrl: page.nextPage,
    };
  };
  const walk = await walkCtmPagination<Record<string, unknown>>(fetchPage);
  return {
    records: walk.records,
    truncated: walk.truncated,
    pagesWalked: walk.pagesWalked,
    totalEntries: walk.totalEntries,
  };
}

export interface FetchCtmSmsMessagesParams {
  readonly accessKey: string;
  readonly secretKey: string;
  readonly region: CtmRegion;
  readonly accountId: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly perPage?: number;
  readonly direction?: "inbound" | "outbound";
  readonly nextPageUrl?: string;
}

export interface CtmSmsMessagesResponse {
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly rowsCharged: number;
  readonly hasNextPage: boolean;
  readonly nextPage: string | null;
}

/**
 * `GET /api/v1/accounts/{account_id}/sms.json`
 *
 * Returns paginated SMS message records. The Postman collection
 * observed 2026-05-15 sometimes uses `sms` and sometimes `messages`
 * as the array key — the helper accepts both with a
 * `ctm_sms_response_key_mismatch` log event on fallback.
 */
export async function fetchCtmSmsMessages(
  params: FetchCtmSmsMessagesParams,
): Promise<CtmSmsMessagesResponse> {
  requireDateRange(params.startDate, params.endDate);
  const perPage = clampPerPage(params.perPage ?? 100);
  const url =
    params.nextPageUrl ?? buildCtmSmsUrl({ ...params, perPage });
  const result = await safeFetchCtm(
    url,
    params.accessKey,
    params.secretKey,
    { method: "GET" },
  );
  if (!result.success) {
    throw wrapTransportError(result.error, params.accessKey, params.secretKey);
  }
  const response = result.data;
  if (!response.ok) {
    const body = await safeReadBody(response);
    throw new CtmApiCallError(
      parseCtmApiError(
        response.status,
        body,
        params.accessKey,
        params.secretKey,
      ),
    );
  }
  const body = (await safeReadJson(response)) as Record<string, unknown> | null;
  if (!body) {
    throw new CtmApiCallError({
      type: "unknown",
      message: sanitizeCtmErrorMessage(
        "CTM /sms.json response was empty or not JSON",
        params.accessKey,
        params.secretKey,
      ),
      status: response.status,
      retryable: false,
    });
  }
  let sms = Array.isArray(body["sms"])
    ? (body["sms"] as ReadonlyArray<Record<string, unknown>>)
    : null;
  if (sms === null && Array.isArray(body["messages"])) {
    console.warn(
      JSON.stringify({
        event: "ctm_sms_response_key_mismatch",
        primary: "sms",
        fallback: "messages",
        timestamp: new Date().toISOString(),
      }),
    );
    sms = body["messages"] as ReadonlyArray<Record<string, unknown>>;
  }
  if (sms === null) sms = [];
  const records = sms.map((row) =>
    transformCtmSmsRow(row, {
      accountId: params.accountId,
    }),
  );
  const nextPage =
    typeof body["next_page"] === "string" &&
    (body["next_page"] as string).length > 0
      ? (body["next_page"] as string)
      : null;
  return {
    records,
    rowsCharged: 1,
    hasNextPage: nextPage !== null,
    nextPage,
  };
}

/**
 * Drain `/sms.json` pagination up to `CTM_PAGINATION_MAX_PAGES`.
 */
export async function fetchAllCtmSmsMessages(
  params: FetchCtmSmsMessagesParams,
): Promise<{
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly truncated: boolean;
  readonly pagesWalked: number;
  readonly totalEntries: number;
}> {
  const perPage = clampPerPage(params.perPage ?? CTM_PER_PAGE_MAX);
  const fetchPage = async (
    nextPageUrl: string | undefined,
  ): Promise<PaginationPageResult<Record<string, unknown>>> => {
    const page = await fetchCtmSmsMessages({
      ...params,
      perPage,
      nextPageUrl,
    });
    return {
      records: page.records,
      nextPageUrl: page.nextPage,
    };
  };
  const walk = await walkCtmPagination<Record<string, unknown>>(fetchPage);
  return {
    records: walk.records,
    truncated: walk.truncated,
    pagesWalked: walk.pagesWalked,
    totalEntries: walk.totalEntries,
  };
}

// ============================================================================
// URL builders
// ============================================================================

function buildCtmCallsUrl(
  p: FetchCtmCallsParams & { perPage: number },
): string {
  const qs = new URLSearchParams();
  qs.set("start_date", p.startDate);
  qs.set("end_date", p.endDate);
  qs.set("per_page", String(p.perPage));
  qs.set(
    "fields",
    computeCtmFields(
      "calls",
      p.conversationIntelligenceEntitled,
      p.leadReactorsEnabled ?? false,
      p.scorecardsEnabled ?? false,
    ),
  );
  return `${baseUrlFor(p.region)}/accounts/${encodeURIComponent(p.accountId)}/calls.json?${qs.toString()}`;
}

function buildCtmFormsUrl(
  p: FetchCtmFormSubmissionsParams & { perPage: number },
): string {
  const qs = new URLSearchParams();
  qs.set("start_date", p.startDate);
  qs.set("end_date", p.endDate);
  qs.set("per_page", String(p.perPage));
  return `${baseUrlFor(p.region)}/accounts/${encodeURIComponent(p.accountId)}/forms.json?${qs.toString()}`;
}

function buildCtmSmsUrl(
  p: FetchCtmSmsMessagesParams & { perPage: number },
): string {
  const qs = new URLSearchParams();
  qs.set("start_date", p.startDate);
  qs.set("end_date", p.endDate);
  qs.set("per_page", String(p.perPage));
  if (p.direction === "inbound" || p.direction === "outbound") {
    qs.set("direction", p.direction);
  }
  return `${baseUrlFor(p.region)}/accounts/${encodeURIComponent(p.accountId)}/sms.json?${qs.toString()}`;
}

// ============================================================================
// Internal helpers
// ============================================================================

function requireDateRange(start: string, end: string): void {
  if (!start || !end) {
    throw new TypeError(
      `CTM helpers require explicit start_date and end_date (ISO 8601 YYYY-MM-DD). Named ranges like "today" or "last_30_days" are NOT used — pre-resolve to absolute dates at the caller (queue scheduler operates in UTC).`,
    );
  }
}

function clampPerPage(requested: number): number {
  if (!Number.isFinite(requested) || requested < 1) return 1;
  if (requested > CTM_PER_PAGE_MAX) {
    throw new TypeError(
      `CTM per_page maximum is ${CTM_PER_PAGE_MAX}; requested ${requested}.`,
    );
  }
  return Math.floor(requested);
}

async function safeReadBody(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return "";
  }
}

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    const text = await response.text();
    if (!text) return null;
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function wrapTransportError(
  err: {
    readonly message: string;
    readonly retryable: boolean;
    readonly providerStatus?: number;
  },
  accessKey: string,
  secretKey: string,
): CtmApiCallError {
  return new CtmApiCallError({
    type: "unknown",
    message: sanitizeCtmErrorMessage(err.message, accessKey, secretKey),
    status: typeof err.providerStatus === "number" ? err.providerStatus : null,
    retryable: err.retryable,
  });
}

// ============================================================================
// Story 22.5 — Recording-related helpers (AC #14, AC #4 step "ctm" branch)
// ============================================================================

/**
 * Probe whether the customer's CTM account has call recording enabled at
 * the ACCOUNT level (Settings > Call Settings > Call Recording).
 *
 * Per `postman.calltrackingmetrics.com/` verified 2026-05-16, the
 * `/api/v1/accounts/{accountId}.json` endpoint returns the account
 * settings including a `record` boolean field.
 *
 * **Return contract:**
 *   - 200 with `record === true` → `true`
 *   - 200 with `record !== true` (any falsy value or missing) → `false`
 *   - 401 / 404 → throws `CtmApiCallError` (caller maps 401 to the standard
 *     token-expired flow; 404 should never happen because the account ID
 *     comes from `discoverCtmAccounts`)
 *   - 5xx / transport error → throws via `wrapTransportError`
 *
 * Story 22.5 AC #14.
 */
export async function probeCtmRecordingsEnabled(
  accessKey: string,
  secretKey: string,
  region: CtmRegion,
  accountId: string,
): Promise<boolean> {
  const url = `${baseUrlFor(region)}/accounts/${encodeURIComponent(accountId)}.json`;
  const result = await safeFetchCtm(url, accessKey, secretKey, {
    method: "GET",
  });
  if (!result.success) {
    throw wrapTransportError(result.error, accessKey, secretKey);
  }
  const response = result.data;
  if (!response.ok) {
    const body = await safeReadBody(response);
    const parsed = parseCtmApiError(response.status, body, accessKey, secretKey);
    throw new CtmApiCallError(parsed);
  }
  const body = (await safeReadJson(response)) as
    | { record?: unknown; account?: { record?: unknown } }
    | null;
  // CTM's `/accounts/{id}.json` returns the settings either at the top level
  // (in current API verified 2026-05-16) or nested under `account.record` in
  // older schema variants. Probe both keys; strict boolean check.
  if (body?.record === true) return true;
  if (
    body?.account &&
    typeof body.account === "object" &&
    body.account.record === true
  ) {
    return true;
  }
  return false;
}

/**
 * Fetch the upstream signed recording URL for a single CTM call.
 *
 * **CRITICAL — this is the only helper in `ctm/api.ts` that touches the
 * single-call endpoint.** The stream-route at
 * `apps/server/src/routes/recordings/stream.ts` is the ONLY route allowed
 * to call this helper. CI grep guards (AC #21) enforce this constraint
 * across the rest of the codebase.
 *
 * Per `postman.calltrackingmetrics.com/` verified 2026-05-16, the endpoint is
 *   `GET /api/v1/accounts/{accountId}/calls/{callId}.json`
 * and the response includes an `audio` field whose value is the signed URL
 * (~30-minute upstream TTL per CTM support).
 *
 * **Return contract:**
 *   - 200 with non-empty `audio` string → returns `{ url }`
 *   - 200 with missing/empty/non-string `audio` → throws (mapped to 404 by caller —
 *     happens when CTM's Expiring Media URLs feature has aged the URL out)
 *   - 401 → throws (mapped to 502 UPSTREAM_AUTH_FAILED by caller)
 *   - 404 → throws (mapped to 404 RECORDING_NOT_FOUND by caller)
 *   - 5xx → throws (caller retries once + maps to 502)
 */
export interface FetchCtmRecordingUrlResult {
  readonly url: string;
}

export async function fetchCtmRecordingUrl(
  accessKey: string,
  secretKey: string,
  region: CtmRegion,
  accountId: string,
  callId: string,
): Promise<FetchCtmRecordingUrlResult> {
  const url = `${baseUrlFor(region)}/accounts/${encodeURIComponent(accountId)}/calls/${encodeURIComponent(callId)}.json`;
  const result = await safeFetchCtm(url, accessKey, secretKey, {
    method: "GET",
  });
  if (!result.success) {
    throw wrapTransportError(result.error, accessKey, secretKey);
  }
  const response = result.data;
  if (!response.ok) {
    const body = await safeReadBody(response);
    const parsed = parseCtmApiError(response.status, body, accessKey, secretKey);
    throw new CtmApiCallError(parsed);
  }
  const body = (await safeReadJson(response)) as
    | { audio?: unknown; call?: { audio?: unknown } }
    | null;
  // Probe both schema variants — top-level `audio` (current) AND nested
  // `call.audio` (older). Strict string check + non-empty.
  let audio: unknown = body?.audio;
  if (
    typeof audio !== "string" &&
    body?.call &&
    typeof body.call === "object"
  ) {
    audio = body.call.audio;
  }
  if (typeof audio !== "string" || audio.length === 0) {
    throw new CtmApiCallError({
      type: "not_found",
      message: "CTM /calls/{id}.json returned no audio field (recording aged out or never existed)",
      status: 404,
      retryable: false,
    });
  }
  return { url: audio };
}

// ============================================================================
// Story 35.4 — NEW endpoint helpers: /triggers.json + /integrations.json
// ============================================================================

/**
 * Detect whether a `/triggers.json` or `/integrations.json` 403/422
 * response body indicates the customer's CTM plan does NOT include the
 * add-on. CTM error bodies aren't strictly schematized; we match
 * conservatively to avoid over-matching benign vendor copy like
 * "Account upgrade in progress" or "Subscription billing failed"
 * (Round 1 Blind Hunter HIGH + Edge Case Hunter MEDIUM finding).
 *
 * **Two-track matcher (Round 1 fix):**
 *
 *  1. **Explicit add-on tokens** — `leadreactors` or `scorecards` in
 *     the body. These tokens only appear in subscription-gating
 *     contexts; matching is unambiguous.
 *  2. **Co-occurrence pattern** — body must contain BOTH a subscription
 *     keyword (`subscription` / `plan` / `add-on`) AND a gating-state
 *     keyword (`required` / `not enabled` / `not allowed` / `upgrade`).
 *     This rejects standalone phrases like "Account upgrade in
 *     progress" (no co-occurrence with subscription/plan/add-on in the
 *     gating sense).
 *
 * False-negatives are recoverable (the helper throws via the normal
 * error path, the orchestration surfaces a warning). False-positives
 * silently flip the entitlement flag to false for 24 hours until the
 * cron re-probe — much more expensive, so we err strict.
 */
const ADD_ON_EXPLICIT_TOKEN_REGEX = /leadreactors|scorecards/i;
const ADD_ON_SUBSCRIPTION_KEYWORD_REGEX = /subscription|\bplan\b|add-?on/i;
// Co-occurrence gating state — looks for any of:
//   - "required" (typical "subscription required" / "Pro plan required")
//   - "not enabled" / "not allowed" / "not available" / etc. (negation phrases)
//   - "does not have ... enabled" — handles "Your plan does not have this add-on enabled"
//   - "must upgrade" / "please upgrade" / "upgrade your" / "upgrade to"
const ADD_ON_GATING_STATE_REGEX = /required|not\s+(enabled|allowed|available|supported|included|active)|does\s+not\s+(have|include|support).*\benabled|must\s+upgrade|please\s+upgrade|upgrade\s+(your|to)/i;

function isAddOnNotEnabledBody(body: string): boolean {
  if (!body || typeof body !== "string") return false;
  // Track 1: explicit add-on token — always matches.
  if (ADD_ON_EXPLICIT_TOKEN_REGEX.test(body)) return true;
  // Track 2: co-occurrence required.
  return (
    ADD_ON_SUBSCRIPTION_KEYWORD_REGEX.test(body) &&
    ADD_ON_GATING_STATE_REGEX.test(body)
  );
}

export interface FetchCtmTriggersParams {
  readonly accessKey: string;
  readonly secretKey: string;
  readonly region: CtmRegion;
  readonly accountId: string;
  readonly perPage?: number;
  readonly nextPageUrl?: string;
}

export interface CtmTriggersResponse {
  readonly records: ReadonlyArray<Record<string, unknown>>;
  /**
   * Story 35.4 graceful-degradation signal — `false` when the customer
   * does NOT have LeadReactors enabled (403/422 with body matching
   * `subscription|plan|upgrade|add-on|leadreactors`). Callers should
   * persist this to `metadata.leadReactorsEnabled` for entitlement
   * gating.
   */
  readonly entitled: boolean;
  readonly hasNextPage: boolean;
  readonly nextPage: string | null;
}

/**
 * `GET /api/v1/accounts/{account_id}/triggers.json`
 *
 * Returns paginated LeadReactor trigger records (Story 35.4 — Batch 4
 * NEW endpoint). On 403 / 422 with body indicating add-on not enabled,
 * returns `{records: [], entitled: false}` per AC #6 graceful
 * degradation — this is the canonical "LeadReactors subscription gate"
 * signal that the route handler persists to
 * `metadata.leadReactorsEnabled` in the `ctm:meta:{integrationId}` KV
 * side-channel.
 *
 * Note: triggers are NOT date-range bound — they reflect the customer's
 * current LeadReactor configuration state (count of active rules).
 * Per AC #6 + Story 35.4 cron-driven re-probe pattern.
 */
export async function fetchCtmTriggers(
  params: FetchCtmTriggersParams,
): Promise<CtmTriggersResponse> {
  const perPage = clampPerPage(params.perPage ?? CTM_PER_PAGE_MAX);
  const url =
    params.nextPageUrl ?? buildCtmTriggersUrl({ ...params, perPage });
  const result = await safeFetchCtm(
    url,
    params.accessKey,
    params.secretKey,
    { method: "GET" },
  );
  if (!result.success) {
    // safeFetch wraps non-ok HTTP responses as {success: false} with
    // `providerStatus` + `providerMessage`. Story 35.4 — Round 1 fix:
    // graceful-degradation path lives HERE (not in the response.ok
    // branch which is unreachable for 4xx/5xx through safeFetch).
    const providerStatus = result.error.providerStatus ?? 0;
    const providerMessage = result.error.providerMessage ?? "";
    if (
      (providerStatus === 403 || providerStatus === 422) &&
      isAddOnNotEnabledBody(providerMessage)
    ) {
      return {
        records: [],
        entitled: false,
        hasNextPage: false,
        nextPage: null,
      };
    }
    throw wrapTransportError(result.error, params.accessKey, params.secretKey);
  }
  const response = result.data;
  const body = (await safeReadJson(response)) as Record<string, unknown> | null;
  if (!body) {
    throw new CtmApiCallError({
      type: "unknown",
      message: sanitizeCtmErrorMessage(
        "CTM /triggers.json response was empty or not JSON",
        params.accessKey,
        params.secretKey,
      ),
      status: response.status,
      retryable: false,
    });
  }
  const triggers = Array.isArray(body["triggers"])
    ? (body["triggers"] as ReadonlyArray<Record<string, unknown>>)
    : [];
  const records = triggers.map((row) =>
    transformCtmTriggerRow(row, { accountId: params.accountId }),
  );
  const nextPage =
    typeof body["next_page"] === "string" &&
    (body["next_page"] as string).length > 0
      ? (body["next_page"] as string)
      : null;
  return {
    records,
    entitled: true,
    hasNextPage: nextPage !== null,
    nextPage,
  };
}

/**
 * Drain `/triggers.json` pagination up to `CTM_PAGINATION_MAX_PAGES`.
 * Mirrors `fetchAllCtmCalls` shape. Returns the union of all records
 * across pages plus a `truncated` flag if the cap was hit.
 *
 * **Round 2 fix — `entitled` semantics clarified:** the FIRST page's
 * `entitled` signal is the authoritative entitlement state (the cron
 * health-probe also uses page 1 only). Subsequent pages cannot change
 * the entitlement state — if page 1 returns entitled: true, the customer
 * has the add-on and page 2..N are walked normally. If page 1 returns
 * entitled: false, the walker short-circuits (page 1 already returned
 * empty records + entitled: false; no further pages exist for a
 * subscription-gated response).
 */
export async function fetchAllCtmTriggers(params: FetchCtmTriggersParams): Promise<{
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly entitled: boolean;
  readonly truncated: boolean;
  readonly pagesWalked: number;
  readonly totalEntries: number;
}> {
  const perPage = clampPerPage(params.perPage ?? CTM_PER_PAGE_MAX);
  // Capture FIRST-page entitled signal only. Subsequent page calls
  // cannot legitimately downgrade the flag (the add-on is either on or
  // off for the whole account — not page-by-page).
  let pageIndex = 0;
  let firstPageEntitled = true;
  const fetchPage = async (
    nextPageUrl: string | undefined,
  ): Promise<PaginationPageResult<Record<string, unknown>>> => {
    const page = await fetchCtmTriggers({ ...params, perPage, nextPageUrl });
    if (pageIndex === 0) firstPageEntitled = page.entitled;
    pageIndex++;
    return {
      records: page.records,
      nextPageUrl: page.nextPage,
    };
  };
  const walk = await walkCtmPagination<Record<string, unknown>>(fetchPage);
  return {
    records: walk.records,
    entitled: firstPageEntitled,
    truncated: walk.truncated,
    pagesWalked: walk.pagesWalked,
    totalEntries: walk.totalEntries,
  };
}

export interface FetchCtmIntegrationsParams {
  readonly accessKey: string;
  readonly secretKey: string;
  readonly region: CtmRegion;
  readonly accountId: string;
  readonly perPage?: number;
  readonly nextPageUrl?: string;
}

export interface CtmIntegrationsResponse {
  readonly records: ReadonlyArray<Record<string, unknown>>;
  /**
   * Story 35.4 — integrations endpoint is "always available" even for
   * base-plan customers (Round 5 Edge Case 5). An empty `records: []`
   * is the expected base-plan response, NOT an error. `entitled` is
   * therefore always `true` UNLESS the API itself returned an
   * add-on-not-enabled 403/422 (defensive — not observed in practice).
   */
  readonly entitled: boolean;
  readonly hasNextPage: boolean;
  readonly nextPage: string | null;
}

/**
 * `GET /api/v1/accounts/{account_id}/integrations.json`
 *
 * Returns the customer's connected 3rd-party integrations (Google Ads,
 * HubSpot, Salesforce, Zapier, etc.) per Story 35.4 Batch 4 NEW
 * endpoint. Not strictly paginated in practice but tolerates `next_page`
 * for forward-compat.
 *
 * Like `/triggers.json`, on 403/422 with an add-on-not-enabled body
 * returns `{records: [], entitled: false}` — defensive even though
 * integrations are NOT publicly documented as a paid add-on at the time
 * of writing (2026-05-21).
 */
export async function fetchCtmIntegrations(
  params: FetchCtmIntegrationsParams,
): Promise<CtmIntegrationsResponse> {
  const perPage = clampPerPage(params.perPage ?? CTM_PER_PAGE_MAX);
  const url =
    params.nextPageUrl ?? buildCtmIntegrationsUrl({ ...params, perPage });
  const result = await safeFetchCtm(
    url,
    params.accessKey,
    params.secretKey,
    { method: "GET" },
  );
  if (!result.success) {
    // safeFetch wraps non-ok HTTP responses as {success: false}. Story
    // 35.4 — Round 1 fix: graceful-degradation path lives in this
    // branch (not in the response.ok branch which is unreachable for
    // 4xx/5xx through safeFetch).
    const providerStatus = result.error.providerStatus ?? 0;
    const providerMessage = result.error.providerMessage ?? "";
    if (
      (providerStatus === 403 || providerStatus === 422) &&
      isAddOnNotEnabledBody(providerMessage)
    ) {
      return {
        records: [],
        entitled: false,
        hasNextPage: false,
        nextPage: null,
      };
    }
    throw wrapTransportError(result.error, params.accessKey, params.secretKey);
  }
  const response = result.data;
  const body = (await safeReadJson(response)) as Record<string, unknown> | null;
  if (!body) {
    throw new CtmApiCallError({
      type: "unknown",
      message: sanitizeCtmErrorMessage(
        "CTM /integrations.json response was empty or not JSON",
        params.accessKey,
        params.secretKey,
      ),
      status: response.status,
      retryable: false,
    });
  }
  const integrations = Array.isArray(body["integrations"])
    ? (body["integrations"] as ReadonlyArray<Record<string, unknown>>)
    : [];
  const records = integrations.map((row) =>
    transformCtmIntegrationRow(row, { accountId: params.accountId }),
  );
  const nextPage =
    typeof body["next_page"] === "string" &&
    (body["next_page"] as string).length > 0
      ? (body["next_page"] as string)
      : null;
  return {
    records,
    entitled: true,
    hasNextPage: nextPage !== null,
    nextPage,
  };
}

/**
 * Drain `/integrations.json` pagination up to `CTM_PAGINATION_MAX_PAGES`.
 * Mirrors `fetchAllCtmTriggers` shape. First-page-wins entitled
 * semantics per Round 2 fix.
 */
export async function fetchAllCtmIntegrations(params: FetchCtmIntegrationsParams): Promise<{
  readonly records: ReadonlyArray<Record<string, unknown>>;
  readonly entitled: boolean;
  readonly truncated: boolean;
  readonly pagesWalked: number;
  readonly totalEntries: number;
}> {
  const perPage = clampPerPage(params.perPage ?? CTM_PER_PAGE_MAX);
  let pageIndex = 0;
  let firstPageEntitled = true;
  const fetchPage = async (
    nextPageUrl: string | undefined,
  ): Promise<PaginationPageResult<Record<string, unknown>>> => {
    const page = await fetchCtmIntegrations({
      ...params,
      perPage,
      nextPageUrl,
    });
    if (pageIndex === 0) firstPageEntitled = page.entitled;
    pageIndex++;
    return {
      records: page.records,
      nextPageUrl: page.nextPage,
    };
  };
  const walk = await walkCtmPagination<Record<string, unknown>>(fetchPage);
  return {
    records: walk.records,
    entitled: firstPageEntitled,
    truncated: walk.truncated,
    pagesWalked: walk.pagesWalked,
    totalEntries: walk.totalEntries,
  };
}

function buildCtmTriggersUrl(
  p: FetchCtmTriggersParams & { perPage: number },
): string {
  const qs = new URLSearchParams();
  qs.set("per_page", String(p.perPage));
  return `${baseUrlFor(p.region)}/accounts/${encodeURIComponent(p.accountId)}/triggers.json?${qs.toString()}`;
}

function buildCtmIntegrationsUrl(
  p: FetchCtmIntegrationsParams & { perPage: number },
): string {
  const qs = new URLSearchParams();
  qs.set("per_page", String(p.perPage));
  return `${baseUrlFor(p.region)}/accounts/${encodeURIComponent(p.accountId)}/integrations.json?${qs.toString()}`;
}
